import { assert } from '@ember/debug';
import { on } from '@ember/modifier';
import Component from '@glimmer/component';

export interface InputControlSignature {
  Element: HTMLInputElement;
  Args: {
    isConfirmed?: boolean;
    hasError?: boolean;
    onChange?: (value: string, e: Event | InputEvent) => void;
    value?: string | null;
  };
}

const STYLES = {
  base: [
    'form-input',
    'text-16',
    'sm:text-14',
    'px-0',
    'py-2',
    'sm:py-0',
    'w-full',
    'bg-transparent',
    'placeholder:text-grey',
    'border-b-grey',
    'border-l-0',
    'border-r-0',
    'border-t-0',
    'focus:border-b-black',
    'focus:ring-0',
    '[&:disabled]:border-b-grey-medium',
    '[&:disabled]:text-grey-medium',
    '[&:disabled]:placeholder:text-transparent',

    // right aligned inputs
    '[appearance:textfield]', // For Firefox
    '[&[type="number"]::-webkit-inner-spin-button]:appearance-none', // For Chrome and Safari
    '[&[type="number"]::-webkit-outer-spin-button]:appearance-none', // For Chrome and Safari
    '[&[type="number"]]:appearance-textfield',
    '[&[type="number"]]:text-right',
    '[&[type="date"]::-webkit-datetime-edit]:justify-end', // For Chrome and Safari
    '[&[type="datetime-local"]::-webkit-datetime-edit]:justify-end' // For Chrome and Safari
  ],
  confirmed: [
    'text-teal',
    'border-b-teal',
    'focus:border-b-teal',
    'focus:shadow-confirmed-input'
  ],
  error: [
    'text-red',
    'border-b-red',
    'focus:border-b-red',
    'focus:shadow-error-input'
  ]
} as const;

export default class WrFormControlsInputComponent extends Component<InputControlSignature> {
  get styles() {
    let styles;

    if (this.args.isConfirmed) styles = [...STYLES.base, ...STYLES.confirmed];
    else if (this.args.hasError) styles = [...STYLES.base, ...STYLES.error];
    else styles = [...STYLES.base, 'focus:shadow-input'];

    return styles.join(' ');
  }

  handleInput = (e: Event | InputEvent): void => {
    assert('Expected HTMLInputElement', e.target instanceof HTMLInputElement);
    this.args.onChange?.(e.target.value, e);
  };

  <template>
    <input
      class={{this.styles}}
      value={{@value}}
      {{on 'input' this.handleInput}}
      ...attributes
    />
  </template>
}
